import i18n from '../i18n';

/**
 * Centralized error translation utility
 * Maps server error messages to translation keys
 */

// Mapping of server error messages to translation keys
const ERROR_MAPPINGS = {
  // Authentication errors
  'Incorrect username or password': 'auth.incorrectCredentials',
  'Login failed': 'auth.loginFailed',
  'Refresh token is required': 'auth.refreshTokenRequired',
  'Invalid refresh token': 'auth.invalidRefreshToken',
  'User not found': 'auth.userNotFound',
  'Token refresh failed': 'auth.tokenRefreshFailed',
  'Account is locked. Please contact an administrator.': 'auth.accountLocked',
  'Account is disabled. Please contact an administrator.':
    'auth.accountDisabled',

  // User management errors
  'Failed to update user': 'auth.failedToUpdateUser',
  'Username already exists': 'auth.usernameAlreadyExists',
  'Email already exists': 'auth.emailAlreadyExists',
  'Failed to create user': 'auth.failedToCreateUser',
  'Failed to retrieve users': 'auth.failedToRetrieveUsers',
  'Failed to retrieve user': 'auth.failedToRetrieveUser',
  'Failed to delete user': 'auth.failedToDeleteUser',
  'Failed to lock user': 'auth.failedToLockUser',
  'Failed to unlock user': 'auth.failedToUnlockUser',

  // Company management errors
  'Company not found': 'auth.companyNotFound',
  'Company name already exists': 'auth.companyNameAlreadyExists',
  'Invalid model name': 'auth.invalidModelName',
  'Temperature must be between 0.0 and 2.0': 'auth.temperatureRange',
  'Max tokens must be at least 1': 'auth.maxTokensRange',
  'Failed to create company': 'auth.failedToCreateCompany',
  'Failed to update company': 'auth.failedToUpdateCompany',
  'Failed to retrieve companies': 'auth.failedToRetrieveCompanies',
  'Unexpected error occurred': 'auth.unexpectedError',

  // PDF management errors
  'Only PDF files are allowed': 'auth.onlyPdfFilesAllowed',
  'File too large. Maximum size is 50MB.': 'auth.fileTooLarge',
  'Failed to upload PDF': 'auth.failedToUploadPdf',
  'PDF file not found': 'auth.pdfFileNotFound',
  'Failed to remove PDF': 'auth.failedToRemovePdf',
  'Failed to list PDFs': 'auth.failedToListPdfs',

  // QA Log errors
  'Failed to list QA logs': 'auth.failedToListQaLogs',
  'QA log not found': 'auth.qaLogNotFound',
  'Failed to retrieve QA log': 'auth.failedToRetrieveQaLog',
  'Database error occurred': 'auth.databaseError',

  // Vector Store errors
  'Failed to get vector store status': 'auth.failedToGetVectorStoreStatus',
  'No PDF documents found for this company': 'auth.noPdfDocumentsFound',
  'Failed to rebuild vector stores': 'auth.failedToRebuildVectorStores',
  'Failed to get vector store size information':
    'auth.failedToGetVectorStoreSize',

  // System errors
  'Redis health check failed': 'auth.redisHealthCheckFailed',
  'Failed to get system status': 'auth.failedToGetSystemStatus',

  // Chat errors
  'Failed to get conversations': 'auth.failedToGetConversations',
  'Conversation not found': 'auth.conversationNotFound',
  'Access denied': 'auth.accessDenied',
  'Failed to get conversation detail': 'auth.failedToGetConversationDetail',
  'Access denied to conversation': 'auth.accessDeniedToConversation',
  'Failed to process chat request': 'auth.failedToProcessChatRequest',
  'Conversation not found or access denied':
    'auth.conversationNotFoundOrAccessDenied',
  'Failed to delete conversation': 'auth.failedToDeleteConversation',

  // OpenAI errors
  'OpenAI vector store sync failed': 'auth.openaiVectorStoreSyncFailed',
  'OpenAI vector store status failed': 'auth.openaiVectorStoreStatusFailed',
  'OpenAI assistant debug failed': 'auth.openaiAssistantDebugFailed',
};

/**
 * Translate a server error message to the current language
 * @param {string} errorMessage - The error message from the server
 * @returns {string} - The translated error message
 */
export const translateError = errorMessage => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return errorMessage;
  }

  // Check if we have a direct mapping for this error message
  const translationKey = ERROR_MAPPINGS[errorMessage];
  if (translationKey) {
    return i18n.t(translationKey);
  }

  // Check for partial matches (for dynamic error messages)
  for (const [serverMessage, key] of Object.entries(ERROR_MAPPINGS)) {
    if (errorMessage.includes(serverMessage)) {
      return i18n.t(key);
    }
  }

  // If no translation found, return the original message
  return errorMessage;
};

/**
 * Translate error response from API
 * @param {Object} errorResponse - The error response object
 * @returns {string} - The translated error message
 */
export const translateApiError = errorResponse => {
  if (!errorResponse) {
    return 'An unknown error occurred';
  }

  // Handle different error response formats
  let errorMessage = 'An unknown error occurred';

  if (typeof errorResponse === 'string') {
    errorMessage = errorResponse;
  } else if (errorResponse.detail) {
    if (typeof errorResponse.detail === 'string') {
      errorMessage = errorResponse.detail;
    } else if (Array.isArray(errorResponse.detail)) {
      // Handle validation error arrays
      errorMessage = errorResponse.detail
        .map(err => err.msg || err.message || 'Validation error')
        .join(', ');
    } else if (errorResponse.detail.error) {
      errorMessage = errorResponse.detail.error;
    }
  } else if (errorResponse.message) {
    errorMessage = errorResponse.message;
  } else if (errorResponse.error) {
    errorMessage = errorResponse.error;
  }

  return translateError(errorMessage);
};

/**
 * Translate error from axios error object
 * @param {Object} axiosError - The axios error object
 * @returns {string} - The translated error message
 */
export const translateAxiosError = axiosError => {
  if (!axiosError) {
    return 'An unknown error occurred';
  }

  // Try to get error from response data first
  if (axiosError.response?.data) {
    return translateApiError(axiosError.response.data);
  }

  // Fall back to error message
  if (axiosError.message) {
    return translateError(axiosError.message);
  }

  return 'An unknown error occurred';
};

/**
 * Extract error message from error response, handling validation errors
 * @param {Object} error - The error object from axios
 * @param {string} fallbackMessage - Fallback message if no error detail found
 * @returns {string} - The error message
 */
export const getErrorMessage = (
  error,
  fallbackMessage = 'An error occurred'
) => {
  if (!error) return fallbackMessage;

  // Try to get detail from response
  const detail = error.response?.data?.detail;

  if (!detail) return fallbackMessage;

  // Handle validation errors (array of objects with {type, loc, msg, ...})
  if (Array.isArray(detail)) {
    const messages = detail
      .map(err => err.msg || err.message || 'Validation error')
      .join(', ');
    return messages || fallbackMessage;
  }

  // Handle string errors
  if (typeof detail === 'string') {
    return detail;
  }

  // Handle object errors
  if (typeof detail === 'object' && detail.message) {
    return detail.message;
  }

  return fallbackMessage;
};

export default {
  translateError,
  translateApiError,
  translateAxiosError,
  getErrorMessage,
};
